import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DOMPurify from 'dompurify';

// Función para convertir HTML a texto plano con formato básico
const htmlToPlainText = (html) => {
  if (!html) return '';
  
  // Crear un elemento temporal para parsear el HTML (sanitizado)
  const temp = document.createElement('div');
  temp.innerHTML = DOMPurify.sanitize(html);
  
  // Función recursiva para procesar nodos
  const processNode = (node) => {
    let result = '';
    
    node.childNodes.forEach(child => {
      if (child.nodeType === Node.TEXT_NODE) {
        result += child.textContent;
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName.toLowerCase();
        
        switch(tagName) {
          case 'p':
            result += processNode(child) + '\n\n';
            break;
          case 'br':
            result += '\n';
            break;
          case 'li':
            result += '• ' + processNode(child) + '\n';
            break;
          case 'ul':
          case 'ol':
            result += '\n' + processNode(child);
            break;
          case 'h1':
          case 'h2':
          case 'h3':
            result += processNode(child).toUpperCase() + '\n\n';
            break;
          case 'strong':
          case 'b':
            result += processNode(child);
            break;
          case 'a':
            result += processNode(child) + ' (' + (child.href || '') + ')';
            break;
          default:
            result += processNode(child);
        }
      }
    });
    
    return result;
  };
  
  return processNode(temp).trim();
};

export const generarPDFGestion = async (gestion) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  
  // Helper function para verificar espacio y agregar página si es necesario
  const checkPageBreak = (neededSpace = 40) => {
    if (yPos > pageHeight - neededSpace) {
      doc.addPage();
      yPos = 20;
    }
  };

  // Mapeo de nombres de campos a etiquetas legibles
  const labelMap = {
    'network': 'Red Social',
    'author': 'Autor o cuenta',
    'tematica': 'Temática relacionada',
    'persons': 'Personas relacionadas',
    'link': 'Enlace publicación',
    'color_bandera': 'Color de bandera',
    'indicador_id': 'Indicador',
    'fecha_inicio_analisis': 'Fecha inicio análisis',
    'fecha_fin_analisis': 'Fecha fin análisis',
    'unidad_compradora': 'Unidad compradora',
    'cantidad_nog': 'Cantidad de NOG',
    'cantidad_npg': 'Cantidad de NPG',
    'enlace_guatecompras': 'Enlace Guatecompras',
    'referencia': 'Referencia',
    'fecha_solicitud': 'Fecha solicitud',
    'numero_oficio': 'Referencia',
    'fecha_oficio': 'Fecha solicitud',
    'fecha_recepcion': 'Fecha recepción',
    'canal_recepcion': 'Canal de recepción',
    'caso_origen': 'Referencia',
    'links_referenciales': 'Enlaces Referenciales'
  };
  
  // Header con logo y título
  doc.setFillColor(102, 126, 234);
  doc.rect(0, 0, pageWidth, 30, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.text('ALERTA 360', 14, 15);
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text('Comisión Nacional contra la Corrupción', 14, 22);
  
  // Nomenclatura y tipo
  let yPos = 40;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  
  // Mostrar URGENTE si aplica
  if (gestion.es_urgente) {
    doc.setFillColor(211, 47, 47); // Rojo
    doc.roundedRect(14, yPos - 6, 25, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text('URGENTE', 16, yPos - 1);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.text(`${gestion.nomenclatura}`, 42, yPos);
  } else {
    doc.text(`${gestion.nomenclatura}`, 14, yPos);
  }
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(gestion.tipo_gestion, gestion.es_urgente ? 42 : 14, yPos + 7);
  
  yPos += 15;
  
  // Línea separadora
  doc.setDrawColor(200, 200, 200);
  doc.line(14, yPos, pageWidth - 14, yPos);
  yPos += 10;

  // ==========================================
  // 1. CREADO POR
  // ==========================================
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text('Creado por:', 14, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(`${gestion.createdBy} | ${new Date(gestion.createdAt).toLocaleDateString('es-ES')}`, 45, yPos);
  yPos += 8;

  // ==========================================
  // 2. DESCRIPCIÓN / HALLAZGO
  // ==========================================
  checkPageBreak(30);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(33, 150, 243);
  doc.text('DESCRIPCIÓN / HALLAZGO', 14, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
  
  // Convertir HTML a texto plano y manejar el margen correctamente
  const descripcionTexto = htmlToPlainText(gestion.descripcion) || 'Sin descripción';
  // Usar un margen más conservador: 14 izquierda + 20 derecha = 34
  const maxWidth = pageWidth - 34;
  const descLines = doc.splitTextToSize(descripcionTexto, maxWidth);
  
  // Verificar salto de página para cada línea
  descLines.forEach((line) => {
    if (yPos > pageHeight - 20) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(line, 14, yPos);
    yPos += 5;
  });
  yPos += 8;

  // ==========================================
  // 3. INFORMACIÓN ESPECÍFICA
  // ==========================================
  if (gestion.campos_especificos && Object.keys(gestion.campos_especificos).length > 0) {
    checkPageBreak(30);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(33, 150, 243);
    doc.text('INFORMACIÓN ESPECÍFICA', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    // Campos simples
    const camposSimples = Object.entries(gestion.campos_especificos).filter(
      ([key, value]) => key !== 'detalle_nogs' && !Array.isArray(value) && typeof value !== 'object'
    );
    
    camposSimples.forEach(([key, value]) => {
      checkPageBreak(20);
      const label = labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      doc.setFont(undefined, 'bold');
      doc.text(`${label}:`, 14, yPos);
      doc.setFont(undefined, 'normal');
      
      const valueStr = typeof value === 'number' ? value.toLocaleString('es-GT') : (value || 'N/A');
      const valueLines = doc.splitTextToSize(String(valueStr), pageWidth - 75);
      doc.text(valueLines, 65, yPos);
      yPos += Math.max(6, valueLines.length * 5);
    });
    
    yPos += 5;
    
    // Enlaces Referenciales (array de links)
    const linksReferenciales = gestion.links_referenciales || gestion.campos_especificos?.links_referenciales;
    if (linksReferenciales && Array.isArray(linksReferenciales) && linksReferenciales.length > 0) {
      checkPageBreak(30);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.text('Enlaces Referenciales:', 14, yPos);
      yPos += 6;
      
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30, 136, 229); // Azul para links
      linksReferenciales.forEach((link, index) => {
        checkPageBreak(15);
        const linkText = `${index + 1}. ${link}`;
        const linkLines = doc.splitTextToSize(linkText, pageWidth - 30);
        doc.text(linkLines, 20, yPos);
        yPos += linkLines.length * 5;
      });
      doc.setTextColor(0, 0, 0);
      yPos += 5;
    }
    
    // Detalle de NOGs (tabla especial para Adquisiciones Públicas)
    const detalleNogs = gestion.campos_especificos.detalle_nogs;
    if (detalleNogs && Array.isArray(detalleNogs) && detalleNogs.length > 0) {
      checkPageBreak(60);
      
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Detalle de NOGs (${detalleNogs.length})`, 14, yPos);
      yPos += 6;
      
      autoTable(doc, {
        startY: yPos,
        head: [['NOG', 'Descripción', 'Tipo', 'Estado', 'Fecha', 'Monto']],
        body: detalleNogs.map(nog => [
          nog.nog || 'N/A',
          nog.descripcion || nog.unidad_compradora || 'N/A',
          nog.tipo_concurso || 'N/A',
          nog.status_concurso || 'N/A',
          nog.fecha_adjudicacion ? new Date(nog.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A',
          nog.monto ? `Q ${parseFloat(nog.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Detalle de NPGs (tabla para Adquisiciones Públicas)
    const detalleNpgs = gestion.campos_especificos.detalle_npgs;
    if (detalleNpgs && Array.isArray(detalleNpgs) && detalleNpgs.length > 0) {
      checkPageBreak(60);
      
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Detalle de NPGs / Concursos (${detalleNpgs.length})`, 14, yPos);
      yPos += 6;
      
      autoTable(doc, {
        startY: yPos,
        head: [['NPG', 'Descripción', 'Tipo', 'Estado', 'Fecha', 'Monto']],
        body: detalleNpgs.map(npg => [
          npg.npg || 'N/A',
          npg.descripcion || 'N/A',
          npg.tipo_concurso || 'N/A',
          npg.status_concurso || 'N/A',
          npg.fecha_adjudicacion ? new Date(npg.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A',
          npg.monto ? `Q ${parseFloat(npg.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [21, 101, 192] },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
    
    // Detalle de Proveedores (tabla para Adquisiciones Públicas)
    const proveedores = gestion.campos_especificos.proveedores;
    if (proveedores && Array.isArray(proveedores) && proveedores.length > 0) {
      checkPageBreak(60);
      
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(`Proveedores (${proveedores.length})`, 14, yPos);
      yPos += 6;
      
      autoTable(doc, {
        startY: yPos,
        head: [['Nombre', 'NIT', 'Oferta (Q)']],
        body: proveedores.map(prov => [
          prov.nombre || 'N/A',
          prov.nit || 'N/A',
          prov.oferta ? `Q ${parseFloat(prov.oferta).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [76, 175, 80] },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
  }

  // ==========================================
  // 4. ANÁLISIS COMPLEMENTARIO (ASESOR CNC) - Con Historial
  // ==========================================
  const historialAnalisis = gestion.historial_analisis || [];
  if (historialAnalisis.length > 0) {
    checkPageBreak(30);
    
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(244, 143, 0);
    doc.text(`ANÁLISIS COMPLEMENTARIO CNC (${historialAnalisis.length})`, 14, yPos);
    yPos += 10;
    
    historialAnalisis.forEach((entrada, idx) => {
      checkPageBreak(25);
      
      // Encabezado de cada análisis
      doc.setFont(undefined, 'bold');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      const fechaAnalisis = new Date(entrada.fecha).toLocaleDateString('es-ES', { 
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
      let encabezado = `[${idx + 1}] ${fechaAnalisis} - ${entrada.usuario}`;
      if (entrada.editado_por) {
        encabezado += ` (Editado por ${entrada.editado_por})`;
      }
      doc.text(encabezado, 14, yPos);
      yPos += 5;
      
      // Texto del análisis - convertir HTML y manejar saltos de página
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const analisisTexto = htmlToPlainText(entrada.texto);
      const analisisLines = doc.splitTextToSize(analisisTexto, pageWidth - 34);
      
      // Verificar salto de página para cada línea
      analisisLines.forEach((line) => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 14, yPos);
        yPos += 5;
      });
      yPos += 6;
    });
    
    yPos += 5;
  } else if (gestion.analisis_complementario) {
    checkPageBreak(30);
    
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.setTextColor(244, 143, 0);
    doc.text('ANÁLISIS COMPLEMENTARIO (ASESOR CNC)', 14, yPos);
    yPos += 8;
    
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    // Convertir HTML a texto plano y manejar márgenes/saltos de página
    const analisisTexto = htmlToPlainText(gestion.analisis_complementario);
    const analisisLines = doc.splitTextToSize(analisisTexto, pageWidth - 34);
    
    // Verificar salto de página para cada línea
    analisisLines.forEach((line) => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, 14, yPos);
      yPos += 5;
    });
    yPos += 8;
  }

  // ==========================================
  // 5. ASIGNACIÓN / ANÁLISIS
  // ==========================================
  checkPageBreak(40);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(33, 150, 243);
  doc.text('ASIGNACIÓN / ANÁLISIS', 14, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
  
  const infoAsignacion = [
    ['Institución:', gestion.institucion],
    ['Asesor:', gestion.nombre_asesor_cnc],
    ['Fecha asignación:', new Date(gestion.fecha_asignacion).toLocaleDateString('es-ES')],
    ['Fecha vencimiento:', gestion.fecha_vencimiento ? new Date(gestion.fecha_vencimiento).toLocaleDateString('es-ES') : 'Sin fecha límite'],
    ['Estado:', gestion.estado]
  ];
  
  infoAsignacion.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, 14, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(value, 65, yPos);
    yPos += 6;
  });
  
  yPos += 5;

  // ==========================================
  // 6. DERIVACIÓN INSTANCIA PROBIDAD
  // ==========================================
  checkPageBreak(50);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  if (gestion.asesor_probidad_nombre) {
    doc.setTextColor(76, 175, 80);
  } else {
    doc.setTextColor(255, 152, 0);
  }
  doc.text('DERIVACIÓN INSTANCIA PROBIDAD', 14, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);
  
  if (gestion.asesor_probidad_nombre) {
    const infoDerivacion = [
      ['Instancia de Probidad:', gestion.asesor_probidad_nombre],
      ['Fecha derivación:', gestion.fecha_asignacion_probidad ? new Date(gestion.fecha_asignacion_probidad).toLocaleDateString('es-ES') : (gestion.fecha_derivacion ? new Date(gestion.fecha_derivacion).toLocaleDateString('es-ES') : 'N/A')],
      ['Fecha vencimiento:', gestion.fecha_vencimiento_probidad ? new Date(gestion.fecha_vencimiento_probidad).toLocaleDateString('es-ES') : 'Sin fecha límite']
    ];
    
    // Agregar información de prórroga si existe
    if (gestion.prorroga_probidad && gestion.prorroga_probidad > 0) {
      infoDerivacion.push(['Prórroga otorgada:', `${gestion.prorroga_probidad} días`]);
      if (gestion.fecha_prorroga_otorgada) {
        infoDerivacion.push(['Fecha prórroga:', new Date(gestion.fecha_prorroga_otorgada).toLocaleDateString('es-ES')]);
      }
    }
    
    infoDerivacion.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, 14, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(value, 70, yPos);
      yPos += 6;
    });
    
    // Calificación si existe
    if (gestion.calificacion_instancia) {
      yPos += 3;
      doc.setFont(undefined, 'bold');
      doc.text('Calificación:', 14, yPos);
      doc.setFont(undefined, 'normal');
      // Mostrar calificación sin caracteres especiales
      doc.text(`${gestion.calificacion_instancia.puntuacion}/5`, 70, yPos);
      yPos += 6;
      
      // Producto obtenido
      if (gestion.producto_obtenido) {
        doc.setFont(undefined, 'bold');
        doc.text('Producto:', 14, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(gestion.producto_obtenido, 70, yPos);
        yPos += 6;
      }
      
      // Observaciones de calificación si existen
      if (gestion.calificacion_instancia.observacion) {
        doc.setFont(undefined, 'bold');
        doc.text('Obs. calificación:', 14, yPos);
        doc.setFont(undefined, 'normal');
        const obsLines = doc.splitTextToSize(gestion.calificacion_instancia.observacion, pageWidth - 80);
        doc.text(obsLines, 70, yPos);
        yPos += Math.max(6, obsLines.length * 5);
      }
    }
  } else {
    doc.setFont(undefined, 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text('Pendiente de derivación a Instancia de Probidad', 14, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 6;
  }
  
  yPos += 5;

  // ==========================================
  // 7. COMENTARIO DE DERIVACIÓN
  // ==========================================
  if (gestion.bitacora && gestion.bitacora.length > 0) {
    const entradaDerivacion = gestion.bitacora.find(b => b.accion === 'DERIVADO_A_PROBIDAD');
    if (entradaDerivacion && entradaDerivacion.detalles) {
      checkPageBreak(30);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(76, 175, 80);
      doc.text('COMENTARIO DE DERIVACIÓN', 14, yPos);
      yPos += 8;
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const comentarioLines = doc.splitTextToSize(entradaDerivacion.detalles, pageWidth - 34);
      doc.text(comentarioLines, 14, yPos);
      yPos += comentarioLines.length * 5 + 8;
    }
  }

  // ==========================================
  // 8. AVANCES REGISTRADOS
  // ==========================================
  if (gestion.avances && gestion.avances.length > 0) {
    checkPageBreak(40);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(33, 150, 243);
    doc.text(`AVANCES REGISTRADOS (${gestion.avances.length})`, 14, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    
    gestion.avances.forEach((avance, index) => {
      checkPageBreak(25);
      
      doc.setFont(undefined, 'bold');
      const fechaAvance = avance.fecha ? new Date(avance.fecha).toLocaleDateString('es-ES') : 'Sin fecha';
      doc.text(`${index + 1}. [${fechaAvance}]`, 14, yPos);
      yPos += 5;
      
      doc.setFont(undefined, 'normal');
      const avanceTexto = typeof avance === 'object' ? (avance.descripcion || avance.texto || JSON.stringify(avance)) : avance;
      const avanceLines = doc.splitTextToSize(avanceTexto, pageWidth - 34);
      doc.text(avanceLines, 14, yPos);
      yPos += avanceLines.length * 4 + 5;
    });
  }

  // ==========================================
  // 9. BITÁCORA DEL CASO
  // ==========================================
  if (gestion.bitacora && gestion.bitacora.length > 0) {
    checkPageBreak(40);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(156, 39, 176);
    doc.text(`BITÁCORA DEL CASO (${gestion.bitacora.length})`, 14, yPos);
    yPos += 8;
    
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    
    // Solo mostrar las últimas 10 entradas para no sobrecargar el PDF
    const entradasMostrar = gestion.bitacora.slice(-10);
    
    entradasMostrar.forEach((entrada) => {
      checkPageBreak(20);
      
      doc.setFont(undefined, 'bold');
      const fechaBitacora = entrada.fecha ? new Date(entrada.fecha).toLocaleString('es-ES') : 'Sin fecha';
      doc.text(`[${fechaBitacora}] ${entrada.accion}`, 14, yPos);
      yPos += 4;
      
      if (entrada.usuario) {
        doc.setFont(undefined, 'normal');
        doc.text(`Por: ${entrada.usuario}`, 14, yPos);
        yPos += 4;
      }
      
      if (entrada.detalles && entrada.accion !== 'DERIVADO_A_PROBIDAD') {
        doc.setFont(undefined, 'normal');
        const detalleLines = doc.splitTextToSize(entrada.detalles, pageWidth - 34);
        doc.text(detalleLines.slice(0, 2), 14, yPos); // Solo mostrar primeras 2 líneas
        yPos += Math.min(detalleLines.length, 2) * 4;
      }
      
      yPos += 3;
    });
  }

  // Pie de página
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generado: ${new Date().toLocaleString('es-ES')} | Página ${i} de ${totalPages}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }
  
  // Guardar PDF
  doc.save(`${gestion.nomenclatura}_Gestion.pdf`);
};
